import { defineComponent as _defineComponent } from 'vue'
import {useRouter} from "vue-router";
import {computed, onBeforeMount, onMounted, reactive, ref, watch} from "vue";
import {useUserServiceStore} from "@/store/userService.store";
import {usePublicSalesStore} from "@/store/publicSales.store";
import Dropdown from "primevue/dropdown";
import {Currency} from "@/models/currency";
import {useTransactionContextStore} from "@/store/transactionContext.store";
import TooltipComponent from "@/components/TooltipComponent.vue";
import CountryFlag from 'vue-country-flag-next';
import C4EIcon from "@/components/commons/C4EIcon.vue";
import {BigDecimal} from "@/models/store/big.decimal";
import {DecCoin} from "@/models/store/common";
import {useConfigurationStore} from "@/store/configuration.store"; // https://www.npmjs.com/package/vue-country-flag-next



export default /*#__PURE__*/_defineComponent({
  __name: 'InvestmentCalculator',
  props: {
  firstInputBlocked: {
    type:  Boolean,
    required: false,
    default: false
  },
  firstInputDefaultValue: {
    type: Number,
    required: false,
    default: 10000
  },
  secondInputDefaultValue: {
    type: Number,
    required: false
  },
  disableStablecoin: {
    type: Boolean,
    required: false,
    default: false
  },
  isDeclaration: {
    type: Boolean,
    required: false,
    default: true
  }
},
  emits: ['onBuy'],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();


const props =  __props;

const emit = __emit;

onBeforeMount(() => {
  usePublicSalesStore().fetchRoundInfo(useConfigurationStore().config.currentPublicSaleRoundId).then(() => {
    const rate =  usePublicSalesStore().getC4eToUSD;
    if(rate) {
      exchangeRate.value = rate;
    }
  });
  onCurrencyChange();
});

onMounted(() => {
  if(props.secondInputDefaultValue) {
    secondValue.amount = new DecCoin(new BigDecimal(props.secondInputDefaultValue),Currency.STABLE);
    secondValueInput.value = secondValue.amount.amount;
    onSecondInputChange();
  } else {
    firstValue.amount = new DecCoin(new BigDecimal(props.firstInputDefaultValue),Currency.C4E);

    if(useTransactionContextStore().orderModalVisible) {
      firstValue.amount = useTransactionContextStore().amountToBuy;
    }
    firstValueInput.value = firstValue.amount.amount;

    onFirstInputChange();
  }


});

const firstValue = reactive({
  amount: new DecCoin(new BigDecimal(0),Currency.C4E),
  currency: Currency.C4E
});

const secondValue = reactive({
  amount: new DecCoin(new BigDecimal(0),props.disableStablecoin ? Currency.USD : Currency.STABLE),
  currency: props.disableStablecoin ? Currency.USD : Currency.STABLE
});

const firstValueInput = ref();
const secondValueInput = ref();

const exchangeRate = ref<BigDecimal>(new BigDecimal(0));

const onFirstInputChange = () => {
  firstValue.amount = new DecCoin(new BigDecimal(firstValueInput.value), firstValue.currency);
  secondValue.amount = new DecCoin(ceilBigDecimal(exchangeRate.value.multiply(firstValue.amount.amount), secondValue.currency), secondValue.currency);
  secondValueInput.value = secondValue.amount.amount;
};

const onSecondInputChange = () => {
  secondValue.amount= new DecCoin(new BigDecimal(secondValueInput.value), secondValue.currency);
  firstValue.amount = new DecCoin(floorBigDecimal(secondValue.amount.amount.divide(exchangeRate.value), firstValue.currency), firstValue.currency);
  firstValueInput.value = firstValue.amount.amount;
};


watch(() => exchangeRate.value, () => {
  secondValue.amount = new DecCoin(ceilBigDecimal(exchangeRate.value.multiply(firstValue.amount.amount), secondValue.currency), secondValue.currency);
  secondValueInput.value = secondValue.amount.amount;
});

watch(() => secondValue.currency, () => {
  onCurrencyChange();
});

const onCurrencyChange = () => {
  if(secondValue.currency == Currency.USDT || secondValue.currency == Currency.USDC || secondValue.currency == Currency.STABLE) {
    const rate =  usePublicSalesStore().getC4eToUSD;
    if(rate) {
      exchangeRate.value = rate;
    }

  } else {
    const requestedAmount = 100;
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json", "Ari10-Widget-Id": "41875703-9ee2-4729-9d51-e574c61467c3" },
      body: JSON.stringify({"offeredCurrencyCode": secondValue.currency, "offeredAmount": requestedAmount })
    };
    fetch("https://xqkzzpmim7.eu-west-1.awsapprunner.com/currencies/USDT/calculate", requestOptions)
      .then(response => response.json())
      .then(data => {
        const c4eTOUSDT = usePublicSalesStore().getC4eToUSD;

        if(c4eTOUSDT != undefined) {
          exchangeRate.value = c4eTOUSDT.multiply(requestedAmount).divide(data.amount);
        }
      });
  }
};

const currencyList = computed(() => {
  if(props.disableStablecoin == false) {
    return [Currency.STABLE, Currency.EUR, Currency.USD, Currency.PLN];
  } else return [ Currency.EUR, Currency.USD, Currency.PLN];
});

const transactionContextStore = useTransactionContextStore();
const router = useRouter();

const onBuy = () => {
  if(!useUserServiceStore().isLoggedIn) {
    router.push({name: 'signIn'});
  } else {
    transactionContextStore.setAmountToBuy(firstValue.amount);
    transactionContextStore.setAmountToPay(secondValue.amount);
    transactionContextStore.setPaymentCurrency(secondValue.currency);
    transactionContextStore.setExchangeRate(exchangeRate.value);
    emit('onBuy');
  }
};

// const round = (number: number, currency: Currency) => {
//   let decimals = 2;
//   if (currency === Currency.STABLE || currency === Currency.C4E) decimals = 6;
//   return Math.ceil(number*10**decimals)/10**decimals;
// };

const ceilBigDecimal = (number: BigDecimal, currency: Currency) => {
  let decimals = 2;
  if (currency === Currency.STABLE || currency === Currency.C4E) decimals = 6;
  return number.ceil(decimals);
};

const floorBigDecimal = (number: BigDecimal, currency: Currency) => {
  let decimals = 2;
  if (currency === Currency.STABLE || currency === Currency.C4E) decimals = 6;
  return number.floor(decimals);
};
const minimumRequired = () => {
  return (usePublicSalesStore().getC4eToUSD.multiply(firstValue.amount.amount).isBiggerThanOrEqualTo(25) && !usePublicSalesStore().getC4eToUSD.multiply(firstValue.amount.amount).isBiggerThanOrEqualTo(10000)) || !props.isDeclaration;
};

const flagSelector = {
  EUR: 'eu',
  USD: 'us',
  PLN: 'pl'
};

const __returned__ = { props, emit, firstValue, secondValue, firstValueInput, secondValueInput, exchangeRate, onFirstInputChange, onSecondInputChange, onCurrencyChange, currencyList, transactionContextStore, router, onBuy, ceilBigDecimal, floorBigDecimal, minimumRequired, flagSelector, get Dropdown() { return Dropdown }, get Currency() { return Currency }, TooltipComponent, get CountryFlag() { return CountryFlag }, C4EIcon }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})